// The Rollout draft's arms: key, engine, profile and share per arm, the
// buckets each takes, and the cascade's rest. Users per arm come from the
// server preview of this draft.

import { useState } from "react";
import type { Distribution, EngineName, Policy, Profile } from "../../services/voiceEngines";
import {
  armRanges,
  clampPct,
  newArmKey,
  rangeText,
  remainder,
  restRange,
  type Draft,
  type DraftArm,
} from "./policy";
import { armColor, ENGINE_LABEL, ENGINE_ORDER } from "./labels";
import { profileChoices } from "./testUsers";

interface Props {
  draft: Draft;
  live: Policy | null;
  profiles: Profile[];
  allowedEngines: string[];
  distribution: Distribution | null;
  onChange: (arms: DraftArm[]) => void;
}

function ProfileSelect({
  arm,
  profiles,
  onChange,
}: {
  arm: DraftArm;
  profiles: Profile[];
  onChange: (profile: string | null) => void;
}) {
  const choices = profileChoices(arm.engine, profiles);
  const builtin = choices.find((p) => p.builtin);
  const stored = choices.filter((p) => !p.builtin);
  // A value the list does not offer (the built-in named explicitly, or an
  // archived or missing profile) stays selectable so the select can show it.
  const known = arm.profile === null || stored.some((p) => p.key === arm.profile);
  const odd = profiles.find((p) => p.key === arm.profile);
  return (
    <select
      className="mono"
      aria-label={`Profile of arm ${arm.key}`}
      value={arm.profile ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
    >
      <option value="">{builtin ? `${builtin.key} (built-in)` : "built-in default"}</option>
      {stored.map((p) => (
        <option key={p.key} value={p.key}>
          {p.key} · v{p.version}
        </option>
      ))}
      {!known && arm.profile && (
        <option value={arm.profile}>
          {arm.profile}
          {odd?.builtin ? "" : odd ? " (archived)" : " (missing)"}
        </option>
      )}
    </select>
  );
}

export function ArmsEditor({ draft, live, profiles, allowedEngines, distribution, onChange }: Props) {
  const [floorsOpen, setFloorsOpen] = useState<Set<number>>(new Set());
  const ranges = armRanges(draft.arms);
  const rest = remainder(draft.arms);
  const liveArms = new Map((live?.arms ?? []).map((a) => [a.key, a]));
  const counts = new Map((distribution?.arms ?? []).map((a) => [a.key, a]));
  const allowed = (e: EngineName) => e === "cascade" || allowedEngines.includes(e);

  const setArm = (i: number, patch: Partial<DraftArm>) =>
    onChange(draft.arms.map((a, j) => (j === i ? { ...a, ...patch } : a)));
  const removeArm = (i: number) => {
    onChange(draft.arms.filter((_, j) => j !== i));
    setFloorsOpen(new Set());
  };
  const addArm = () => {
    const engine = ENGINE_ORDER.find((e) => e !== "cascade" && allowed(e)) ?? "gemini_live";
    onChange([
      ...draft.arms,
      { key: newArmKey(draft.arms), engine, profile: null, pct: 0, min_build_ios: "", min_build_android: "" },
    ]);
  };
  const toggleFloors = (i: number) =>
    setFloorsOpen((s) => {
      const next = new Set(s);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  const barLabel = [
    ...draft.arms.map((a) => `${a.key} ${a.pct}%`),
    `cascade ${Math.max(rest, 0)}%`,
  ].join(", ");

  return (
    <>
      <div className="table-scroll">
        <table className="guide-table ve-arms" aria-label="Arms">
          <colgroup>
            <col style={{ width: "17%" }} />
            <col style={{ width: "17%" }} />
            <col style={{ width: "21%" }} />
            <col style={{ width: "23%" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "5%" }} />
          </colgroup>
          <thead>
            <tr>
              <th className="no-sort">Arm</th>
              <th className="no-sort">Engine</th>
              <th className="no-sort">Profile</th>
              <th className="no-sort">Share</th>
              <th className="no-sort">Buckets</th>
              <th className="no-sort">Users</th>
              <th className="no-sort">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {draft.arms.map((arm, i) => {
              const was = liveArms.get(arm.key.trim());
              const wasText = !live ? "" : !was ? "new arm" : was.pct !== arm.pct ? `live: ${was.pct}%` : "";
              const count = counts.get(arm.key.trim());
              const hasFloors = arm.min_build_ios !== "" || arm.min_build_android !== "";
              const showFloors = hasFloors || floorsOpen.has(i);
              return [
                <tr key={`arm-${i}`}>
                  <td>
                    <div className="ve-arm-key">
                      <span className="ve-dot" style={{ background: armColor(i) }} aria-hidden="true" />
                      <input
                        type="text"
                        className="mono"
                        aria-label="Arm key"
                        value={arm.key}
                        onChange={(e) => setArm(i, { key: e.target.value.replace(/\s+/g, "-") })}
                      />
                    </div>
                    <button
                      type="button"
                      className="ve-link"
                      aria-expanded={showFloors}
                      disabled={hasFloors}
                      onClick={() => toggleFloors(i)}
                      title={hasFloors ? "Clear both floors to hide them" : "Build floors for this arm only"}
                    >
                      {hasFloors ? "own build floors" : showFloors ? "hide build floors" : "build floors…"}
                    </button>
                  </td>
                  <td>
                    <select
                      aria-label={`Engine of arm ${arm.key}`}
                      value={arm.engine}
                      onChange={(e) => setArm(i, { engine: e.target.value as EngineName, profile: null })}
                    >
                      {ENGINE_ORDER.map((e) => (
                        <option key={e} value={e}>
                          {ENGINE_LABEL[e]}
                          {allowed(e) ? "" : " (not allowed here)"}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <ProfileSelect arm={arm} profiles={profiles} onChange={(profile) => setArm(i, { profile })} />
                  </td>
                  <td>
                    <div className="ve-share">
                      <input
                        type="range"
                        min={0}
                        max={100}
                        step={1}
                        aria-label={`Share of arm ${arm.key}`}
                        value={arm.pct}
                        onChange={(e) => setArm(i, { pct: clampPct(Number(e.target.value)) })}
                      />
                      <input
                        type="number"
                        min={0}
                        max={100}
                        aria-label={`Share of arm ${arm.key}, percent`}
                        value={arm.pct}
                        onChange={(e) => setArm(i, { pct: clampPct(Number(e.target.value)) })}
                      />
                    </div>
                    <div className="ve-was">{wasText}</div>
                  </td>
                  <td className="mono muted">{rangeText(ranges[i])}</td>
                  <td className="av-num">
                    {count ? count.users.toLocaleString() : "—"}
                    {count && count.served < count.users && (
                      <div className="ve-small" style={{ color: "var(--warn)" }}>
                        {count.served.toLocaleString()} served
                      </div>
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-ghost"
                      aria-label={`Remove arm ${arm.key}`}
                      title="Remove this arm"
                      onClick={() => removeArm(i)}
                    >
                      ✕
                    </button>
                  </td>
                </tr>,
                showFloors && (
                  <tr key={`floors-${i}`} className="ve-floors">
                    <td colSpan={7}>
                      <div className="ve-fields">
                        <div>
                          <label htmlFor={`arm-ios-${i}`}>Arm {arm.key}: min iOS build</label>
                          <input
                            id={`arm-ios-${i}`}
                            type="number"
                            min={0}
                            placeholder={`policy's (${draft.min_build_ios || "—"})`}
                            value={arm.min_build_ios}
                            onChange={(e) => setArm(i, { min_build_ios: e.target.value })}
                          />
                        </div>
                        <div>
                          <label htmlFor={`arm-android-${i}`}>Arm {arm.key}: min Android build</label>
                          <input
                            id={`arm-android-${i}`}
                            type="number"
                            min={0}
                            placeholder={`policy's (${draft.min_build_android || "—"})`}
                            value={arm.min_build_android}
                            onChange={(e) => setArm(i, { min_build_android: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="ve-help" style={{ marginLeft: 18 }}>
                        Blank uses the policy's floor. Only an engine that needs a newer app sets its own.
                      </div>
                    </td>
                  </tr>
                ),
              ];
            })}
            <tr>
              <td>
                <div className="ve-arm-key">
                  <span className="ve-dot rest" aria-hidden="true" />
                  <span className="mono muted">rest</span>
                </div>
              </td>
              <td className="muted">Cascade</td>
              <td className="muted mono">cascade-default</td>
              <td>
                <span className="ve-num" style={{ color: rest < 0 ? "var(--danger)" : undefined }}>
                  {rest >= 0 ? `${rest}%` : `over by ${-rest}%`}
                </span>
                <span className="muted ve-small"> · whatever the arms leave</span>
              </td>
              <td className="mono muted">{rangeText(restRange(draft.arms))}</td>
              <td className="av-num">{distribution ? distribution.rest.toLocaleString() : "—"}</td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>
      <div className="ve-bar" role="img" aria-label={barLabel}>
        {draft.arms.map((a, i) =>
          a.pct > 0 ? <span key={i} style={{ width: `${Math.min(a.pct, 100)}%`, background: armColor(i) }} /> : null,
        )}
        {rest > 0 && <span style={{ width: `${rest}%`, background: "var(--panel-3)" }} />}
      </div>
      <div className="ve-help" style={{ marginBottom: 10 }}>
        Buckets 0–99, walked in arm order. Users counts are the last 30 days' voice users who reach the bucket step.
      </div>
      <button type="button" className="btn" onClick={addArm}>
        + Add arm
      </button>
    </>
  );
}
